import { useEffect, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type { Session } from '@supabase/supabase-js';
import { installMfaResponseObserver, sessionNeedsCode } from '@/lib/mfa';
import { login, logout, useAuthStore } from '@/store/auth';

/**
 * Поставщик входа. Только email + пароль (общий вход UNIA через Supabase).
 *
 * Состояние живёт в Zustand (правило проекта: глобальное — не в Context), а
 * этот компонент лишь подписывается на сессию и кладёт её в хранилище.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const setUser = useAuthStore((s) => s.setUser);
  const setMfaPending = useAuthStore((s) => s.setMfaPending);

  useEffect(() => {
    let живо = true;
    // Вход по коду: сессия только по паролю у человека с кодом — ещё не вход.
    // Человек остаётся на экране входа и вводит код; серверы такую сессию всё
    // равно не примут (401 MFA_REQUIRED).
    const принять = (session: Session | null) => {
      if (sessionNeedsCode(session)) {
        setMfaPending(true);
        setUser(null);
        return;
      }
      setMfaPending(false);
      setUser(session?.user ?? null);
    };

    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (живо) принять(session);
    }).catch(() => { if (живо) setUser(null); });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      принять(session);
    });
    // Сервер ответил «нужен код входа» — показать шаг кода, а не выкидывать на вход.
    const снять = installMfaResponseObserver(() => {
      setMfaPending(true);
      setUser(null);
    });
    return () => { живо = false; subscription.unsubscribe(); снять(); };
  }, [setUser, setMfaPending]);

  return <>{children}</>;
}

export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const mfaPending = useAuthStore((s) => s.mfaPending);
  return { user, isLoading, mfaPending, login, logout };
}
